import { Collaborator } from '@/app/models/collaborator';
import TruncatedTextWithTooltip from '@/app/utils/truncatedTextWithTooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, TextField } from '@mui/material';
import TableCell from '@mui/material/TableCell';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable, { bodyCellSmallSx, bodyCellSx, Column } from '../common/DataTable';

interface SpecificAllocation {
  collaborator: Collaborator;
  allocation: number;
  isFinal: boolean;
}

interface NewSpecificAllocationTableProps {
  itens: SpecificAllocation[];
  onRemove: (id: number) => void;
  editSelectedCollaborators: SpecificAllocation[];
  setEditSelectedCollaborators: React.Dispatch<React.SetStateAction<SpecificAllocation[]>>;
}

export default function NewSpecificAllocationTable({
  itens,
  onRemove,
  editSelectedCollaborators,
  setEditSelectedCollaborators,
}: NewSpecificAllocationTableProps) {
  const { t } = useTranslation(['specificAllocation', 'common']);

  const handleDelete = (id: number) => {
    if (id) {
      onRemove(id);
    }
  };
  const [inputValues, setInputValues] = useState<{ [key: number]: string }>({});

  const handlePercentChange = (value: string, item: SpecificAllocation) => {
    const changedValue = value.replace(',', '.');

    // Regex: allows only numbers with up to 2 decimal places
    if (/^\d*([.,]\d{0,2})?$/.test(value)) {
      // Atualiza o valor do input temporário
      setInputValues((prev) => ({
        ...prev,
        [item.collaborator.id]: value,
      }));

      const numericValue = parseFloat(changedValue);

      // If it's a valid number, updates the main state
      if (!Number.isNaN(numericValue)) {
        setEditSelectedCollaborators((prev) => {
          const exists = prev.find((e) => e.collaborator.id === item.collaborator.id);

          // If the collaborator already exists, update only the allocation
          if (exists) {
            return prev.map((e) =>
              e.collaborator.id === item.collaborator.id ? { ...e, allocation: numericValue } : e,
            );
          }
          // If the collaborator doesn't exist yet, add to the list
          return [
            ...prev,
            {
              collaborator: item.collaborator,
              allocation: numericValue,
              isFinal: item.isFinal ?? false,
            },
          ];
        });
      }
    }
  };

  const columns: Column[] = [
    { key: 'collaborators', label: t('common:collaborator') },
    { key: 'percent', label: t('common:percent') },
    { key: 'final', label: t('final') },
    {
      key: 'actions',
      label: t('common:actions'),
      align: 'right',
    },
  ];

  const renderRow = (item: SpecificAllocation) => {
    return (
      <>
        <TableCell sx={bodyCellSx}>
          <TruncatedTextWithTooltip
            sx={bodyCellSx}
            value={`${item.collaborator.badge} - ${item.collaborator.name}`}
            size={30}
          />
        </TableCell>

        <TableCell sx={bodyCellSx}>
          <TextField
            value={
              inputValues[item.collaborator.id] ??
              editSelectedCollaborators
                .find((e) => e.collaborator.id === item.collaborator.id)
                ?.allocation.toFixed(2) ??
              item.allocation.toFixed(2)
            }
            onChange={(e) => handlePercentChange(e.target.value, item)}
            size="small"
            inputProps={{ style: { border: 'none', width: '60px' } }}
          />
        </TableCell>

        <TableCell sx={bodyCellSx}>{item.isFinal ? t('common:yes') : t('common:no')}</TableCell>

        <TableCell align="right" sx={bodyCellSmallSx}>
          <IconButton size="small" onClick={() => handleDelete(item.collaborator.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </>
    );
  };

  return (
    <DataTable
      columns={columns}
      data={itens}
      renderRow={renderRow}
      getRowKey={(item) => item.collaborator.id}
      emptyMessage={t('common:no.results.found')}
      size="small"
      stickyHeader
      maxHeight="25rem"
    />
  );
}
